import { taskModelKind } from '@dbrain/shared';
import { Alert, Badge, Button, ConfirmDialog, Dialog, Field, Icons, Menu, MenuItem, PortalPanel, ScrollArea, Select, Switch, TextInput } from '@dbrain/ui';
import { useMutation } from '@tanstack/react-query';
import { useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { modelName, TASK_GROUPS, type TaskState } from '@dbrain/app-core/model-tasks';

export { inheritedLabel, modelName, modelUsage, ROUTE_TASKS, TASK_GROUPS, taskStates, type TaskState } from '@dbrain/app-core/model-tasks';

/**
 * Providers, their models and the model per task (Phase 13g, D-136; PlatformModeller, KurumsalModeller,
 * PlatformSaglayiciEkle): one set of parts for the bireysel operator's Platform, the kurumsal Yönetim → Modeller,
 * a bireysel company's Modeller and a member's own providers. Providers are stacked full width, each with a model
 * table whose columns line up from one provider to the next; "Sağlayıcı ekle" is a dialog whose "Bağlantıyı dene"
 * answers before "Ekle". Keys are typed once and never shown again (QW-612); a model's name comes from the
 * settings, never from code (D-137).
 */

export interface ModelRow {
  id: string;
  modelId: string;
  displayName: string | null;
  kind: 'chat' | 'embedding' | 'rerank' | 'ocr' | 'guard';
  contextWindow: { window: number; assumed: boolean };
  contextWindowOverride: number | null;
  maxOutputTokens: number | null;
  thinking: 'unknown' | 'none' | 'template_toggle' | 'effort_toggle' | 'always';
  supportsTools: boolean | null;
  supportsVision: boolean | null;
  enabled: boolean;
  missingSince: string | null;
  priceInputMicros: number;
  priceOutputMicros: number;
  /** 16c.1: the kind came from the name ("adından tahmin"); "Sohbette"; kurumsal "Kimler kullanabilir" */
  kindGuessed?: boolean;
  chatOffered?: boolean;
  chatAudience?: 'everyone' | 'teams';
  teamIds?: string[];
  /** the picker's line under the name */
  description?: string | null;
  /** discovered and nobody decided about it yet ("Yeni") */
  isNew?: boolean;
  /** looks like this model already open in chat (the same id or the same served model) */
  duplicateOf?: { id: string; name: string; providerName: string } | null;
}

export interface ProviderView {
  id: string;
  scope: 'platform' | 'tenant' | 'user';
  name: string;
  kind: 'openai' | 'openai_compatible' | 'custom';
  baseUrl: string | null;
  hasKey: boolean;
  keyRequired: boolean;
  enabled: boolean;
  status: 'untested' | 'ok' | 'error';
  lastTestedAt: string | null;
}

/** Where a card sends its changes. */
export interface ProviderEndpoints {
  test: string;
  refresh: string;
  update: string;
  remove: string;
  model: (modelId: string) => string;
}

interface TestResult {
  ok: boolean;
  checks?: { name: string; ok: boolean; detail?: string }[];
  warnings?: string[];
  models?: { modelId: string }[];
  modelTested?: string | null;
  code?: string;
  detail?: string;
}

// A provider -----------------------------------------------------------------------------------------------

function CapabilityChips({ model }: { model: ModelRow }) {
  const { t } = useTranslation();
  const thinking = model.thinking === 'unknown' ? null : model.thinking !== 'none';
  const chip = (name: string, value: boolean | null, testId?: string) => (
    <li
      className={`cap-chip cap-chip--${value === null ? 'unknown' : value ? 'yes' : 'no'}`}
      title={value === null ? t('models.notChecked') : undefined}
      data-testid={testId}
    >
      <span aria-hidden="true">{value === null ? '?' : value ? '✓' : '✕'}</span> {name}
      <span className="visually-hidden"> {value === null ? t('models.notChecked') : value ? t('common.yes') : t('common.no')}</span>
    </li>
  );
  return (
    <ul className="cap-chips" data-testid={`capabilities-${model.modelId}`}>
      {chip(t('models.tools'), model.supportsTools, 'cap-tools')}
      {chip(t('models.thinking'), thinking)}
      {chip(t('models.vision'), model.supportsVision)}
    </ul>
  );
}

function TestOutcome({ result }: { result: TestResult }) {
  const { t } = useTranslation();
  return (
    <Alert tone={result.ok ? 'success' : 'error'} title={result.ok ? t('models.test.ok') : t('models.test.failed')} data-testid="provider-test-result">
      {result.detail ? <p>{t(`errors.${result.code ?? 'unknown'}.title`, { defaultValue: result.detail })}</p> : null}
      <ul className="chips">
        {(result.checks ?? []).map((check) => (
          <li key={check.name} className="chip" title={check.detail}>
            {t(`models.test.checks.${check.name}`)} {check.ok ? '✓' : '✕'}
          </li>
        ))}
      </ul>
      {(result.warnings ?? []).map((w) => (
        <p key={w} className="muted">
          {t(`models.test.warnings.${w}`, { defaultValue: w })}
        </p>
      ))}
    </Alert>
  );
}

/**
 * The model's name over its id; once when they are the same. 16c.1 (B2cModelYeni): a model nobody decided about yet
 * shows its id with "Yeni" and its display name under it, and one that looks like a model already open in chat says so.
 */
function ModelNameCell({ model }: { model: ModelRow }) {
  const { t } = useTranslation();
  const name = model.displayName?.trim();
  const badge = model.isNew ? (
    <span data-testid="model-new">
      <Badge tone="info">{t('models.offer.new')}</Badge>
    </span>
  ) : null;
  const twin = model.duplicateOf ? (
    <span className="model-name__warning" data-testid="model-duplicate">
      {t('models.offer.duplicate', { name: model.duplicateOf.name })}
    </span>
  ) : null;
  if (model.isNew) {
    return (
      <span className="model-name">
        <span className="model-name__line">
          <code className="model-name__only">{model.modelId}</code>
          {badge}
        </span>
        {twin}
        {name && name !== model.modelId ? <span className="model-name__id">{t('models.offer.displayName', { name })}</span> : null}
      </span>
    );
  }
  return name && name !== model.modelId ? (
    <span className="model-name">
      <strong className="model-name__display">{name}</strong>
      <code className="model-name__id">{model.modelId}</code>
      {twin}
    </span>
  ) : (
    <span className="model-name">
      <code className="model-name__only">{model.modelId}</code>
      {twin}
    </span>
  );
}

/** How a card shows "Sohbette" (16c.1): bireysel names the packages, kurumsal chooses who may use a model */
export interface ChatOfferContext {
  /** bireysel: the packages listing each model (a model in one is not closed from here) */
  plans?: Readonly<Record<string, readonly ('free' | 'pro')[]>>;
  /** kurumsal: the company's teams ("Kimler kullanabilir") */
  teams?: readonly { id: string; name: string; members: number }[];
  /** kurumsal: the Sohbet and Code tasks' model stays open to everyone */
  lockedOpen?: ReadonlySet<string>;
}

const MODEL_KINDS = ['chat', 'embedding', 'rerank', 'ocr', 'guard'] as const;

/** "Kimler kullanabilir" (KurumsalModelSohbet): Herkes, or the chosen teams with their people; Vazgeç or Uygula */
function AudienceMenu({ model, teams, busy, onApply }: { model: ModelRow; teams: readonly { id: string; name: string; members: number }[]; busy: boolean; onApply: (body: { chatAudience: 'everyone' | 'teams'; teamIds: string[] }) => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDetailsElement>(null);
  const group = useId();
  const current = model.chatAudience ?? 'everyone';
  const [audience, setAudience] = useState<'everyone' | 'teams'>(current);
  const [chosen, setChosen] = useState<string[]>(model.teamIds ?? []);
  const close = () => {
    if (ref.current) ref.current.open = false;
  };
  const label = current === 'teams' ? t('models.offer.teamsCount', { count: model.teamIds?.length ?? 0 }) : t('models.offer.everyone');
  return (
    <details
      ref={ref}
      className="ui-menu audience-menu"
      onToggle={(e) => {
        // each opening starts from what is saved
        if ((e.currentTarget as HTMLDetailsElement).open) {
          setAudience(current);
          setChosen(model.teamIds ?? []);
        }
      }}
      data-testid={`audience-${model.modelId}`}
    >
      <summary className="audience-menu__trigger" aria-label={t('models.offer.audienceOf', { name: modelName(model), who: label })}>
        <Icons.members size={14} aria-hidden="true" />
        <span>{label}</span>
        <Icons.chevronDown size={14} aria-hidden="true" />
      </summary>
      <PortalPanel menu={ref} align="left" className="audience-panel" data-testid={`audience-${model.modelId}-panel`}>
        <p className="ui-menu-label">{t('models.offer.audience')}</p>
        <label className={audience === 'everyone' ? 'audience-option audience-option--on' : 'audience-option'}>
          <input type="radio" name={group} checked={audience === 'everyone'} onChange={() => setAudience('everyone')} data-testid="audience-everyone" />
          <span className="audience-option__text">
            <strong>{t('models.offer.everyone')}</strong>
            <span>{t('models.offer.everyoneHint')}</span>
          </span>
        </label>
        <label className={audience === 'teams' ? 'audience-option audience-option--on' : 'audience-option'}>
          <input type="radio" name={group} checked={audience === 'teams'} onChange={() => setAudience('teams')} data-testid="audience-teams" />
          <span className="audience-option__text">
            <strong>{t('models.offer.chosenTeams')}</strong>
            <span>{t('models.offer.chosenTeamsHint')}</span>
          </span>
        </label>
        {audience === 'teams' ? (
          teams.length > 0 ? (
            <ul className="audience-teams">
              {teams.map((team) => (
                <li key={team.id}>
                  <label className="audience-team">
                    <input
                      type="checkbox"
                      checked={chosen.includes(team.id)}
                      onChange={(e) => setChosen(e.target.checked ? [...chosen, team.id] : chosen.filter((id) => id !== team.id))}
                      data-testid={`audience-team-${team.name}`}
                    />
                    <span className="audience-option__text">
                      <strong>{team.name}</strong>
                      <span>{t('models.offer.people', { count: team.members })}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <p className="audience-panel__empty">{t('models.offer.noTeams')}</p>
          )
        ) : null}
        <div className="audience-panel__actions">
          <Button size="sm" variant="secondary" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            busy={busy}
            disabled={audience === 'teams' && chosen.length === 0}
            onClick={() => {
              onApply({ chatAudience: audience, teamIds: audience === 'teams' ? chosen : [] });
              close();
            }}
            data-testid="audience-apply"
          >
            {t('models.offer.apply')}
          </Button>
        </div>
      </PortalPanel>
    </details>
  );
}

/** A model's details the provider may not report, and its name (D-137). */
function ModelDetailsDialog({ model, endpoint, onSaved, onClose }: { model: ModelRow; endpoint: string; onSaved: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const initial = {
    displayName: model.displayName ?? '',
    description: model.description ?? '',
    window: model.contextWindowOverride ? String(model.contextWindowOverride) : '',
    maxOutput: model.maxOutputTokens ? String(model.maxOutputTokens) : '',
    priceIn: model.priceInputMicros ? String(model.priceInputMicros / 1e6) : '',
    priceOut: model.priceOutputMicros ? String(model.priceOutputMicros / 1e6) : '',
    thinking: model.thinking,
    tools: model.supportsTools === null ? 'unknown' : model.supportsTools ? 'yes' : 'no',
  };
  const [form, setForm] = useState(initial);
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
  // only what changed is sent (the audit log names it)
  const changes = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    if (form.displayName.trim() !== initial.displayName.trim()) out.displayName = form.displayName.trim() || null;
    if (form.description.trim() !== initial.description.trim()) out.description = form.description.trim() || null;
    if (form.window !== initial.window) out.contextWindowOverride = form.window.trim() ? Number(form.window) : null;
    if (form.maxOutput !== initial.maxOutput) out.maxOutputTokens = form.maxOutput.trim() ? Number(form.maxOutput) : null;
    if (form.priceIn !== initial.priceIn) out.priceInputMicros = Math.round(Number(form.priceIn || 0) * 1e6);
    if (form.priceOut !== initial.priceOut) out.priceOutputMicros = Math.round(Number(form.priceOut || 0) * 1e6);
    if (form.thinking !== initial.thinking) out.thinking = form.thinking;
    if (form.tools !== initial.tools) out.supportsTools = form.tools === 'unknown' ? null : form.tools === 'yes';
    return out;
  };
  const save = useMutation({
    mutationFn: async () => {
      const body = changes();
      if (Object.keys(body).length > 0) await api('PATCH', endpoint, body);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const toggle = useMutation({ mutationFn: () => api('PATCH', endpoint, { enabled: !model.enabled }), onSuccess: onSaved });
  return (
    <Dialog
      title={t('models.details.dialogTitle', { name: modelName(model) })}
      onClose={onClose}
      closeLabel={t('common.close')}
      size="wide"
      data-testid={`model-details-${model.modelId}`}
      footer={
        <>
          <Button variant="ghost" className="ui-dialog__footer-start" busy={toggle.isPending} onClick={() => toggle.mutate()} data-testid="model-toggle">
            {model.enabled ? t('models.details.disable') : t('models.details.enable')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={formId} busy={save.isPending} data-testid="model-save">
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <ErrorAlert error={save.error ?? toggle.error} />
        <p className="muted">
          <code>{model.modelId}</code> · {t(`models.kinds.${model.kind}`)}
          {!model.enabled ? ` · ${t('models.disabled')}` : ''}
        </p>
        <Field label={t('models.details.displayName')} hint={t('models.details.displayNameHint', { id: model.modelId })}>
          {({ id, describedBy }) => (
            <TextInput id={id} aria-describedby={describedBy} value={form.displayName} maxLength={120} onChange={(e) => set({ displayName: e.target.value })} data-testid="model-display-name" />
          )}
        </Field>
        {model.kind === 'chat' ? (
          <>
            {/* 16c.1 (B2cModelSecici): the picker's line under the name */}
            <Field label={t('models.details.description')} hint={t('models.details.descriptionHint')}>
              {({ id, describedBy }) => (
                <TextInput id={id} aria-describedby={describedBy} value={form.description} maxLength={120} onChange={(e) => set({ description: e.target.value })} data-testid="model-description" />
              )}
            </Field>
            <div className="form-row">
              <Field label={t('models.details.window')} hint={t('models.details.windowHint', { detected: model.contextWindow.window })}>
                {({ id, describedBy }) => (
                  <TextInput id={id} aria-describedby={describedBy} type="number" min={1024} value={form.window} onChange={(e) => set({ window: e.target.value })} data-testid="model-window" />
                )}
              </Field>
              <Field label={t('models.details.maxOutput')}>
                {({ id }) => <TextInput id={id} type="number" min={1024} value={form.maxOutput} onChange={(e) => set({ maxOutput: e.target.value })} data-testid="model-max-output" />}
              </Field>
            </div>
            <div className="form-row">
              <Field label={t('models.details.thinking')}>
                {({ id }) => (
                  <Select id={id} value={form.thinking} onChange={(e) => set({ thinking: e.target.value as ModelRow['thinking'] })}>
                    {(['unknown', 'none', 'template_toggle', 'effort_toggle', 'always'] as const).map((mode) => (
                      <option key={mode} value={mode}>
                        {t(`models.thinkingModes.${mode}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('models.details.tools')}>
                {({ id }) => (
                  <Select id={id} value={form.tools} onChange={(e) => set({ tools: e.target.value })}>
                    <option value="unknown">{t('models.details.toolsUnknown')}</option>
                    <option value="yes">{t('models.details.toolsYes')}</option>
                    <option value="no">{t('models.details.toolsNo')}</option>
                  </Select>
                )}
              </Field>
            </div>
          </>
        ) : null}
        <div className="form-row">
          <Field label={t('models.details.priceIn')} hint={t('models.details.priceHint')}>
            {({ id, describedBy }) => (
              <TextInput id={id} aria-describedby={describedBy} type="number" min={0} step="0.01" value={form.priceIn} onChange={(e) => set({ priceIn: e.target.value })} data-testid="model-price-in" />
            )}
          </Field>
          <Field label={t('models.details.priceOut')}>
            {({ id }) => <TextInput id={id} type="number" min={0} step="0.01" value={form.priceOut} onChange={(e) => set({ priceOut: e.target.value })} data-testid="model-price-out" />}
          </Field>
        </div>
      </form>
    </Dialog>
  );
}

function ModelTableRow({
  model,
  endpoints,
  tasks,
  onChanged,
  offer,
}: {
  model: ModelRow;
  endpoints: ProviderEndpoints | null;
  tasks: string[];
  onChanged: () => void;
  offer: ChatOfferContext | undefined;
}) {
  const { t } = useTranslation();
  const [details, setDetails] = useState(false);
  const check = useMutation({
    mutationFn: () => api<TestResult>('POST', (endpoints as ProviderEndpoints).test, { modelId: model.modelId }),
    onSuccess: onChanged,
  });
  // 16c.1: the kind, "Sohbette" and who may use it, each saved at once (and in the log, old → new)
  const change = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('PATCH', (endpoints as ProviderEndpoints).model(model.id), body),
    onSuccess: onChanged,
  });
  const taskName = (task: string) => t(`models.routes.tasks.${task}`);
  const chat = model.kind === 'chat';
  const plans = offer?.plans?.[model.id] ?? [];
  const fixedOpen = offer?.lockedOpen?.has(model.id) ?? false;
  // a model in a package (bireysel) or the Sohbet task's (kurumsal) stays in chat; a model serving a task keeps its kind
  const keepOpen = model.chatOffered === true && (plans.length > 0 || fixedOpen);
  const keepKind = tasks.length > 0 || (model.chatOffered === true && plans.length > 0);
  return (
    <>
      <tr className={model.enabled && !model.missingSince ? undefined : 'provider-models__row--off'} data-testid={`model-${model.modelId}`}>
        <td>
          <ModelNameCell model={model} />
        </td>
        <td data-testid="model-kind">
          {endpoints && !keepKind ? (
            <span className="model-kind">
              <Select
                size="sm"
                value={model.kind}
                aria-label={t('models.offer.kindOf', { name: modelName(model) })}
                disabled={change.isPending}
                onChange={(e) => change.mutate({ kind: e.target.value })}
                data-testid="model-kind-select"
              >
                {MODEL_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`models.kinds.${kind}`)}
                  </option>
                ))}
              </Select>
              {model.kindGuessed ? <span className="model-kind__guess">{t('models.offer.guessed')}</span> : null}
            </span>
          ) : (
            <span title={keepKind && endpoints ? t('models.offer.kindFixed') : undefined}>{t(`models.kinds.${model.kind}`)}</span>
          )}
        </td>
        <td data-testid="model-usage">
          {tasks.length > 0 ? (
            <span className="task-chips">
              <span className="task-chip">{taskName(tasks[0] as string)}</span>
              {tasks.length > 1 ? (
                <span className="task-chip" title={tasks.slice(1).map(taskName).join(', ')}>
                  {t('models.table.moreTasks', { count: tasks.length - 1 })}
                </span>
              ) : null}
            </span>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
        <td data-testid="model-chat">
          {!chat ? (
            <span className="muted">{t('models.offer.notChat')}</span>
          ) : (
            <span className="model-offer">
              <span className="model-offer__switch">
                <Switch
                  checked={model.chatOffered === true}
                  disabled={!endpoints || change.isPending || keepOpen}
                  onChange={(e) => change.mutate({ chatOffered: e.target.checked })}
                  aria-label={t('models.offer.switchOf', { name: modelName(model) })}
                  title={keepOpen ? t(fixedOpen ? 'models.offer.keepTask' : 'models.offer.keepPlan') : undefined}
                  data-testid="model-offer"
                />
                {offer?.teams && model.chatOffered && endpoints ? (
                  fixedOpen ? (
                    <span className="audience-menu__trigger audience-menu__trigger--fixed" title={t('models.offer.keepTask')}>
                      <Icons.members size={14} aria-hidden="true" />
                      {t('models.offer.everyone')}
                    </span>
                  ) : (
                    <AudienceMenu model={model} teams={offer.teams} busy={change.isPending} onApply={(body) => change.mutate(body)} />
                  )
                ) : (
                  <span className="model-offer__state">{model.chatOffered ? t('models.offer.on') : t('models.offer.off')}</span>
                )}
              </span>
              {offer?.plans && plans.length > 0 ? (
                <span className="model-offer__plans" data-testid="model-plans">
                  {plans.map((plan) => t(`b2cPlatform.planNames.${plan}`)).join(' · ')}
                </span>
              ) : null}
            </span>
          )}
        </td>
        <td>
          {chat ? <CapabilityChips model={model} /> : null}
          {/* the window is in Ayrıntılar now; one the provider did not tell is said here */}
          {chat && model.contextWindow.assumed ? (
            <span title={t('models.windowAssumed')}>
              <Badge tone="warning">{t('models.offer.windowAssumed')}</Badge>
            </span>
          ) : null}
          {model.missingSince ? <Badge tone="warning">{t('models.missing')}</Badge> : null}
          {!model.enabled ? <Badge tone="warning">{t('models.disabled')}</Badge> : null}
        </td>
        <td className="provider-models__actions">
          {endpoints ? (
            <Menu trigger={<Icons.more size={16} />} label={t('models.table.more', { name: modelName(model) })} align="right" portal triggerClassName="ui-icon-button ui-icon-button--sm" data-testid="model-menu">
              <MenuItem icon={<Icons.sliders size={14} />} onClick={() => setDetails(true)} data-testid="model-edit">
                {t('models.details.title')}
              </MenuItem>
              {chat ? (
                <MenuItem icon={<Icons.plug size={14} />} onClick={() => check.mutate()} data-testid="model-check">
                  {t('models.check')}
                </MenuItem>
              ) : null}
            </Menu>
          ) : null}
        </td>
      </tr>
      {check.data || check.error || check.isPending || change.error ? (
        <tr className="provider-models__result">
          <td colSpan={6}>
            {check.isPending ? <p className="muted">{t('models.offer.checking')}</p> : null}
            {check.data ? <TestOutcome result={check.data} /> : null}
            <ErrorAlert error={check.error ?? change.error} />
          </td>
        </tr>
      ) : null}
      {details && endpoints ? <ModelDetailsDialog model={model} endpoint={endpoints.model(model.id)} onSaved={onChanged} onClose={() => setDetails(false)} /> : null}
    </>
  );
}

/** The provider's name, address and key; a key typed here replaces the saved one, which is never shown. */
function EditProviderDialog({ provider, endpoint, onSaved, onClose }: { provider: ProviderView; endpoint: string; onSaved: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const formId = useId();
  const [name, setName] = useState(provider.name);
  const [baseUrl, setBaseUrl] = useState(provider.baseUrl ?? '');
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(provider.enabled);
  const [clearKey, setClearKey] = useState(false);
  const update = useMutation({
    mutationFn: () =>
      api('PATCH', endpoint, {
        ...(name.trim() !== provider.name ? { name: name.trim() } : {}),
        ...(baseUrl.trim() && baseUrl.trim() !== provider.baseUrl ? { baseUrl: baseUrl.trim() } : {}),
        ...(apiKey ? { apiKey } : clearKey ? { clearApiKey: true } : {}),
        ...(enabled !== provider.enabled ? { enabled } : {}),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <Dialog
      title={t('models.provider.editTitle')}
      onClose={onClose}
      closeLabel={t('common.close')}
      size="wide"
      data-testid="provider-edit-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={formId} busy={update.isPending} disabled={!name.trim()} data-testid="provider-save">
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate();
        }}
      >
        <ErrorAlert error={update.error} />
        <Field label={t('models.add.name')}>{({ id }) => <TextInput id={id} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-testid="provider-edit-name" />}</Field>
        {provider.baseUrl !== null ? (
          <Field label={t('models.add.baseUrl')} hint={t('models.add.baseUrlHint')}>
            {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} className="mono" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />}
          </Field>
        ) : null}
        <Field label={t('models.add.newKey')} hint={provider.hasKey ? t('models.add.newKeyHint') : t('models.add.keyHint')}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="off"
              value={apiKey}
              placeholder={t('models.add.keyPlaceholder')}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="provider-edit-key"
            />
          )}
        </Field>
        {provider.hasKey && !apiKey ? (
          <label className="check-card">
            <input type="checkbox" checked={clearKey} onChange={(e) => setClearKey(e.target.checked)} />
            <span>
              <strong>{t('models.add.clearKey')}</strong>
              <span className="muted">{t('models.add.clearKeyHint')}</span>
            </span>
          </label>
        ) : null}
        <label className="switch-row">
          <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="provider-enabled" />
          <span>
            <strong>{t('models.provider.enabled')}</strong>
            <span className="muted"> — {t('models.provider.enabledHint')}</span>
          </span>
        </label>
      </form>
    </Dialog>
  );
}

/**
 * One provider (D-136): its name and type, address, key and connection, its actions, and a table of its models
 * with the same columns as every other provider's.
 */
export function ProviderCard({
  provider,
  models,
  endpoints,
  onChanged,
  usage,
  scopeLabel,
  offer,
}: {
  provider: ProviderView;
  models: ModelRow[];
  /** null: read only (the platform's provider on a company's page) */
  endpoints: ProviderEndpoints | null;
  onChanged: () => void;
  /** the tasks each model serves */
  usage: ReadonlyMap<string, string[]>;
  /** "Platform" next to the name, where the page shows other levels' providers too */
  scopeLabel?: string | undefined;
  /** 16c.1: how "Sohbette" shows (packages in bireysel, teams in kurumsal) */
  offer?: ChatOfferContext | undefined;
}) {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const test = useMutation({ mutationFn: () => api<TestResult>('POST', (endpoints as ProviderEndpoints).test, {}), onSuccess: onChanged });
  const refresh = useMutation({
    mutationFn: () => api<{ added: string[]; missing: string[] }>('POST', (endpoints as ProviderEndpoints).refresh, {}),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', (endpoints as ProviderEndpoints).remove),
    onSuccess: () => {
      setConfirmDelete(false);
      onChanged();
    },
  });
  const usable = models.filter((m) => !m.missingSince);
  const status =
    provider.status === 'ok'
      ? t('models.provider.connected', { count: usable.length })
      : provider.status === 'error'
        ? t('models.provider.error')
        : t('models.provider.untested');
  const tone = provider.status === 'ok' ? 'success' : provider.status === 'error' ? 'danger' : 'quiet';
  // Sil names the tasks that would stop (D-136)
  const tasksOfProvider = [...new Set(models.flatMap((m) => usage.get(m.id) ?? []))];
  return (
    <article className="provider-card" data-testid={`provider-${provider.name}`}>
      <header className="provider-card__head">
        <span className="provider-card__icon" aria-hidden="true">
          <Icons.models size={18} />
        </span>
        <div className="provider-card__main">
          <div className="provider-card__title">
            <h3 className="provider-card__name">{provider.name}</h3>
            {scopeLabel ? <span className="tag-pill">{scopeLabel}</span> : null}
            <span className="tag-pill">{t(`models.providerKinds.${provider.kind}`)}</span>
            {!provider.enabled ? <Badge tone="warning">{t('models.disabled')}</Badge> : null}
          </div>
          <p className="provider-card__meta">
            {provider.baseUrl ? <code className="provider-card__url">{provider.baseUrl}</code> : null}
            {/* QW-601: a provider without a key says so */}
            <span className={`provider-card__key${provider.hasKey ? '' : ' provider-card__key--none'}`}>
              <Icons.key size={13} aria-hidden="true" />
              {provider.hasKey ? t('models.provider.keySaved') : t('models.provider.noKey')}
            </span>
            <span className={`provider-card__status provider-card__status--${tone}`} data-testid="provider-status">
              <span className="provider-card__dot" aria-hidden="true" />
              {status}
            </span>
            {provider.lastTestedAt ? (
              <span className="provider-card__tested">
                {t('models.provider.testedAt', { date: new Date(provider.lastTestedAt).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }) })}
              </span>
            ) : null}
          </p>
        </div>
        {endpoints ? (
          <div className="provider-card__actions">
            <Button size="sm" variant="secondary" icon={<Icons.plug size={14} />} busy={test.isPending} onClick={() => test.mutate()} data-testid="provider-test">
              {t('models.provider.test')}
            </Button>
            <Button size="sm" variant="secondary" icon={<Icons.regenerate size={14} />} busy={refresh.isPending} onClick={() => refresh.mutate()} data-testid="provider-refresh">
              {t('models.provider.refresh')}
            </Button>
            <Menu trigger={<Icons.more size={16} />} label={t('models.provider.more', { name: provider.name })} align="right" portal triggerClassName="ui-icon-button ui-icon-button--sm" data-testid="provider-menu">
              <MenuItem icon={<Icons.edit size={14} />} onClick={() => setEditing(true)} data-testid="provider-edit">
                {t('models.provider.edit')}
              </MenuItem>
              <MenuItem icon={<Icons.trash size={14} />} danger onClick={() => setConfirmDelete(true)} data-testid="provider-delete">
                {t('common.delete')}
              </MenuItem>
            </Menu>
          </div>
        ) : null}
      </header>
      {test.error ?? refresh.error ? (
        <div className="provider-card__notes">
          <ErrorAlert error={test.error ?? refresh.error} />
        </div>
      ) : null}
      {test.data || refresh.data ? (
        <div className="provider-card__notes">
          {test.data ? <TestOutcome result={test.data} /> : null}
          {refresh.data ? (
            <Alert tone="success" title={t('models.provider.refreshed', { added: refresh.data.added.length, missing: refresh.data.missing.length })} data-testid="provider-refreshed" />
          ) : null}
        </div>
      ) : null}
      {models.length === 0 ? (
        <p className="provider-card__empty">{t('models.provider.noModels')}</p>
      ) : (
        // a phone scrolls the table sideways: the region takes keyboard focus (WCAG 2.1.1)
        <ScrollArea label={t('models.table.caption', { name: provider.name })} className="provider-models__wrap">
          <table className="provider-models" data-testid="provider-models">
            <caption className="visually-hidden">{t('models.table.caption', { name: provider.name })}</caption>
            <colgroup>
              <col className="provider-models__col--model" />
              <col className="provider-models__col--kind" />
              <col className="provider-models__col--usage" />
              <col className="provider-models__col--chat" />
              <col className="provider-models__col--caps" />
              <col className="provider-models__col--actions" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">{t('models.table.model')}</th>
                <th scope="col">{t('models.table.kind')}</th>
                <th scope="col">{t('models.table.usage')}</th>
                <th scope="col">{t('models.table.chat')}</th>
                <th scope="col">{t('models.table.capabilities')}</th>
                <th scope="col">
                  <span className="visually-hidden">{t('models.table.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {/* 16c.1 (B2cModelYeni): the chat models first, then the task models (Gömme, Yeniden sıralama, …) */}
              {[...models]
                .sort((a, b) => Number(a.kind !== 'chat') - Number(b.kind !== 'chat'))
                .map((m) => (
                  <ModelTableRow key={m.id} model={m} endpoints={endpoints} tasks={usage.get(m.id) ?? []} onChanged={onChanged} offer={offer} />
                ))}
            </tbody>
          </table>
        </ScrollArea>
      )}
      {editing && endpoints ? <EditProviderDialog provider={provider} endpoint={endpoints.update} onSaved={onChanged} onClose={() => setEditing(false)} /> : null}
      {confirmDelete && endpoints ? (
        <ConfirmDialog
          title={t('models.provider.deleteTitle', { name: provider.name })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={remove.isPending}
          error={<ErrorAlert error={remove.error} />}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirmDelete(false)}
          data-testid="provider-delete-dialog"
        >
          <p>{t('models.provider.deleteBody', { count: models.length })}</p>
          {tasksOfProvider.length > 0 ? (
            <p data-testid="provider-delete-tasks">
              {t('models.provider.deleteTasks', { count: tasksOfProvider.length, tasks: tasksOfProvider.map((task) => t(`models.routes.tasks.${task}`)).join(', ') })}
            </p>
          ) : (
            <p className="muted">{t('models.provider.deleteNoTasks')}</p>
          )}
        </ConfirmDialog>
      ) : null}
    </article>
  );
}

/** "Sağlayıcılar · 2 sağlayıcı · 9 model · sohbette 1" over the stacked cards. */
export function ProviderList({ providers, models, offered, children }: { providers: number; models: number; offered?: number; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <section className="provider-list" aria-labelledby="providers-title">
      <div className="section-head">
        <h2 id="providers-title" className="section-head__title">
          {t('models.list.title')}
        </h2>
        <span className="section-head__note" data-testid="providers-count">
          {t('models.list.providers', { count: providers })} · {t('models.list.models', { count: models })}
          {offered !== undefined ? ` · ${t('models.list.offered', { count: offered })}` : null}
        </span>
      </div>
      {children}
    </section>
  );
}

// Adding a provider -------------------------------------------------------------------------------------------

export interface ProviderDraft {
  name: string;
  kind: 'openai_compatible' | 'openai' | 'custom';
  baseUrl?: string;
  apiKey?: string;
  modelId?: string;
  capacityManaged?: boolean;
  logOptOut?: boolean;
}

/**
 * A new provider (MM-001, MM-003; PlatformSaglayiciEkle): any OpenAI-compatible server (vLLM, LiteLLM) or OpenAI
 * itself. "Bağlantıyı dene" reaches it with what is typed and says what it found before anything is saved; it is
 * not required (D-136). `installation`: the GPU options of the installation's own servers.
 */
function ProviderForm({
  formId,
  installation,
  onTest,
  onCreate,
  onDone,
  footer,
}: {
  formId: string;
  installation: boolean;
  onTest: (draft: ProviderDraft) => Promise<TestResult>;
  onCreate: (draft: ProviderDraft) => Promise<unknown>;
  onDone: () => void;
  footer: (state: { testing: boolean; creating: boolean; canTest: boolean; canCreate: boolean; test: () => void }) => ReactNode;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<ProviderDraft['kind']>('openai_compatible');
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [modelId, setModelId] = useState('');
  const [capacityManaged, setCapacityManaged] = useState(false);
  const [logOptOut, setLogOptOut] = useState(false);
  const address = baseUrl.trim() || (kind === 'openai' ? 'https://api.openai.com/v1' : '');
  const draft = (): ProviderDraft => ({
    name: name.trim(),
    kind,
    ...(address ? { baseUrl: address } : {}),
    ...(apiKey ? { apiKey } : {}),
    ...(kind === 'custom' && modelId.trim() ? { modelId: modelId.trim() } : {}),
    ...(installation ? { capacityManaged, logOptOut } : {}),
  });
  const test = useMutation({ mutationFn: () => onTest(draft()) });
  const create = useMutation({ mutationFn: () => onCreate(draft()), onSuccess: onDone });
  // a result answers what was typed; a change asks again
  const touched = () => test.reset();
  const found = test.data?.models ?? [];
  return (
    <form
      id={formId}
      className="stack provider-form"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <ErrorAlert error={create.error} />
      <div className="form-row">
        <Field label={t('models.add.kind')}>
          {({ id }) => (
            <Select
              id={id}
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as ProviderDraft['kind']);
                touched();
              }}
              data-testid="provider-kind"
            >
              <option value="openai_compatible">{t('models.providerKinds.openai_compatible')}</option>
              <option value="openai">{t('models.providerKinds.openai')}</option>
              <option value="custom">{t('models.providerKinds.custom')}</option>
            </Select>
          )}
        </Field>
        <Field label={t('models.add.name')}>
          {({ id }) => <TextInput id={id} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder={t('models.add.namePlaceholder')} data-testid="provider-name" />}
        </Field>
      </div>
      <Field label={t('models.add.baseUrl')} hint={kind === 'openai' ? t('models.add.openaiHint') : t('models.add.baseUrlHint')}>
        {({ id, describedBy }) => (
          <TextInput
            id={id}
            aria-describedby={describedBy}
            className="mono"
            value={baseUrl}
            onChange={(e) => {
              setBaseUrl(e.target.value);
              touched();
            }}
            placeholder={kind === 'openai' ? 'https://api.openai.com/v1' : 'http://10.0.0.12:8000/v1'}
            data-testid="provider-url"
          />
        )}
      </Field>
      {kind === 'custom' ? (
        <Field label={t('models.add.modelId')}>
          {({ id }) => (
            <TextInput
              id={id}
              className="mono"
              value={modelId}
              onChange={(e) => {
                setModelId(e.target.value);
                touched();
              }}
            />
          )}
        </Field>
      ) : null}
      <Field label={t('models.add.key')} hint={t('models.add.keyHint')}>
        {({ id, describedBy }) => (
          <TextInput
            id={id}
            aria-describedby={describedBy}
            type="password"
            autoComplete="off"
            value={apiKey}
            placeholder={t('models.add.keyPlaceholder')}
            onChange={(e) => {
              setApiKey(e.target.value);
              touched();
            }}
            data-testid="provider-key"
          />
        )}
      </Field>
      {installation ? (
        <div className="check-cards">
          <label className="check-card">
            <input type="checkbox" checked={capacityManaged} onChange={(e) => setCapacityManaged(e.target.checked)} data-testid="provider-capacity" />
            <span>
              <strong>{t('models.add.capacityManaged')}</strong>
              <span className="muted">{t('models.add.capacityManagedHint')}</span>
            </span>
          </label>
          <label className="check-card">
            <input type="checkbox" checked={logOptOut} onChange={(e) => setLogOptOut(e.target.checked)} />
            <span>
              <strong>{t('models.add.logOptOut')}</strong>
              <span className="muted">{t('models.add.logOptOutHint')}</span>
            </span>
          </label>
        </div>
      ) : null}
      {test.data ? (
        test.data.ok ? (
          <p className="provider-form__result provider-form__result--ok" role="status" data-testid="provider-draft-result">
            <Icons.check size={16} aria-hidden="true" />
            <span>
              {found.length > 0 ? t('models.add.testFound', { count: found.length }) : t('models.add.testOk')}
              {found.length > 0 ? (
                <strong className="mono">
                  {found
                    .slice(0, 3)
                    .map((m) => m.modelId)
                    .join(', ')}
                  {found.length > 3 ? ` +${found.length - 3}` : ''}
                </strong>
              ) : null}
            </span>
          </p>
        ) : (
          <div role="status" data-testid="provider-draft-result">
            <TestOutcome result={test.data} />
          </div>
        )
      ) : null}
      <ErrorAlert error={test.error} />
      {footer({
        testing: test.isPending,
        creating: create.isPending,
        canTest: Boolean(address),
        // a name and an address (OpenAI's own when left empty), and OpenAI's key (QW-601)
        canCreate: Boolean(name.trim()) && Boolean(address) && (kind !== 'openai' || Boolean(apiKey)) && (kind !== 'custom' || Boolean(modelId.trim())),
        test: () => test.mutate(),
      })}
    </form>
  );
}

/** "Sağlayıcı ekle" as a dialog (D-136): no form stays on the page. */
export function AddProviderDialog({
  installation,
  onTest,
  onCreate,
  onClose,
}: {
  installation: boolean;
  onTest: (draft: ProviderDraft) => Promise<TestResult>;
  onCreate: (draft: ProviderDraft) => Promise<unknown>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const formId = useId();
  return (
    <Dialog title={t('models.add.title')} closeLabel={t('common.close')} onClose={onClose} size="wide" data-testid="provider-add-dialog">
      <ProviderForm
        formId={formId}
        installation={installation}
        onTest={onTest}
        onCreate={onCreate}
        onDone={onClose}
        footer={({ testing, creating, canTest, canCreate, test }) => (
          <div className="provider-form__footer">
            <Button variant="secondary" icon={<Icons.plug size={14} />} busy={testing} disabled={!canTest} onClick={test} data-testid="provider-try">
              {t('models.add.test')}
            </Button>
            <span className="provider-form__footer-end">
              <Button variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" busy={creating} disabled={!canCreate} data-testid="provider-add">
                {t('models.add.submit')}
              </Button>
            </span>
          </div>
        )}
      />
    </Dialog>
  );
}

/** A member's own provider in the settings, where a dialog over the dialog would not do: the same form, in place. */
export function AddProviderInline({ onTest, onCreate }: { onTest: (draft: ProviderDraft) => Promise<TestResult>; onCreate: (draft: ProviderDraft) => Promise<unknown> }) {
  const { t } = useTranslation();
  const formId = useId();
  const [round, setRound] = useState(0);
  return (
    <section className="provider-inline" aria-labelledby={`${formId}-title`} data-testid="add-provider">
      <h3 id={`${formId}-title`} className="section-head__title">
        {t('models.add.title')}
      </h3>
      <ProviderForm
        key={round}
        formId={formId}
        installation={false}
        onTest={onTest}
        onCreate={onCreate}
        onDone={() => setRound(round + 1)}
        footer={({ testing, creating, canTest, canCreate, test }) => (
          <div className="provider-form__footer">
            <Button variant="secondary" icon={<Icons.plug size={14} />} busy={testing} disabled={!canTest} onClick={test} data-testid="provider-try">
              {t('models.add.test')}
            </Button>
            <span className="provider-form__footer-end">
              <Button type="submit" busy={creating} disabled={!canCreate} data-testid="provider-add">
                {t('models.add.submit')}
              </Button>
            </span>
          </div>
        )}
      />
    </section>
  );
}

// The model per task -------------------------------------------------------------------------------------

/**
 * "Görevlere göre model" (D-136): one table, grouped (Ana işler, Yardımcı işler, Belge araması), one choice per
 * task. A task without a choice of its own shows, muted, what it runs on: "Ana model · Qwen3.8 27B".
 */
export function TaskModels({
  models,
  states,
  inherited,
  caption,
  onSet,
  testId,
}: {
  /** the models this level may choose, with their provider's name */
  models: (ModelRow & { providerName: string })[];
  states: Record<string, TaskState>;
  /** the muted words of a task without a choice of its own: where its model comes from */
  inherited: (task: string, state: TaskState) => string;
  caption: string;
  onSet: (task: string, modelRef: string | null) => Promise<unknown>;
  testId: string;
}) {
  const { t } = useTranslation();
  const set = useMutation({ mutationFn: ({ task, modelRef }: { task: string; modelRef: string | null }) => onSet(task, modelRef) });
  const usable = models.filter((m) => m.enabled && !m.missingSince);
  // reading scanned pages is a task only where a model can do it (D-136)
  const shown = (task: string) => task !== 'ocr' || usable.some((m) => m.kind === 'ocr') || Boolean(states.ocr?.effective);
  return (
    <section className="task-models" aria-labelledby={`${testId}-title`} data-testid={testId}>
      <div className="section-head">
        <h2 id={`${testId}-title`} className="section-head__title">
          {t('models.routes.title')}
        </h2>
        <span className="section-head__note">{caption}</span>
      </div>
      <ErrorAlert error={set.error} />
      <div className="task-table__wrap">
        <table className="task-table">
          <thead>
            <tr>
              <th scope="col">{t('models.routes.task')}</th>
              <th scope="col">{t('models.routes.model')}</th>
            </tr>
          </thead>
          {TASK_GROUPS.map((group) => (
            <tbody key={group.id} data-testid={`task-group-${group.id}`}>
              <tr className="task-table__group">
                <th scope="colgroup" colSpan={2}>
                  <strong>{t(`models.routes.groups.${group.id}.title`)}</strong>
                  <span> · {t(`models.routes.groups.${group.id}.hint`)}</span>
                </th>
              </tr>
              {group.tasks.filter(shown).map((task) => {
                const state = states[task] ?? { own: null, effective: null, fallback: { modelRef: null, source: 'none' as const } };
                const options = usable.filter((m) => m.kind === taskModelKind(task));
                // a choice that is no longer usable stays visible, so the table never claims another model
                const current = state.own && !options.some((m) => m.id === state.own) ? models.find((m) => m.id === state.own) : undefined;
                return (
                  <tr key={task} data-testid={`route-${task}`}>
                    <th scope="row">
                      <strong>{t(`models.routes.tasks.${task}`)}</strong>
                      <span className="task-table__hint"> · {t(`models.routes.hints.${task}`)}</span>
                    </th>
                    <td>
                      <Select
                        className={state.own ? undefined : 'task-table__inherited'}
                        aria-label={t('models.routes.choose', { task: t(`models.routes.tasks.${task}`) })}
                        value={state.own ?? ''}
                        disabled={set.isPending}
                        onChange={(e) => set.mutate({ task, modelRef: e.target.value || null })}
                        data-testid={`route-select-${task}`}
                      >
                        <option value="">{inherited(task, state)}</option>
                        {current ? (
                          <option value={current.id} disabled>
                            {modelName(current)} · {current.providerName} ({t('models.disabled')})
                          </option>
                        ) : null}
                        {options.map((m) => (
                          <option key={m.id} value={m.id}>
                            {modelName(m)} · {m.providerName}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
}
